import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

export const useQuickInfo = (propertyId = null) => {
    const { user } = useAuth();
    const [quickInfo, setQuickInfo] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchQuickInfo = useCallback(async () => {
        if (!user) {
            setLoading(false);
            return;
        }

        let query = supabase
            .from("quick_info")
            .select("*");

        // Filter by property if provided
        if (propertyId) {
            query = query.eq("property_id", propertyId);
        }

        const { data, error } = await query;

        if (error) {
            console.error("Error fetching quick info:", error);
        } else {
            setQuickInfo(data || []);
        }
        setLoading(false);
    }, [user, propertyId]);

    const upsertQuickInfo = async (
        infoType,
        label,
        value,
        subValue
    ) => {
        if (!user) return;

        const existingItem = quickInfo.find((item) => item.info_type === infoType);

        if (existingItem) {
            const { error } = await supabase
                .from("quick_info")
                .update({
                    value,
                    sub_value: subValue || null,
                    updated_at: new Date().toISOString(),
                })
                .eq("id", existingItem.id);

            if (error) {
                console.error("Error updating quick info:", error);
                return;
            }
        } else {
            const { error } = await supabase.from("quick_info").insert({
                user_id: user.id,
                info_type: infoType,
                label,
                value,
                sub_value: subValue || null,
                property_id: propertyId || null,
            });

            if (error) {
                console.error("Error inserting quick info:", error);
                return;
            }
        }

        fetchQuickInfo();
    };

    const deleteQuickInfo = async (id) => {
        if (!user) return false;

        const { error } = await supabase
            .from("quick_info")
            .delete()
            .eq("id", id);

        if (error) {
            console.error("Error deleting quick info:", error);
            return false;
        }

        fetchQuickInfo();
        return true;
    };

    useEffect(() => {
        let active = true;
        const timer = setTimeout(() => {
            if (active) {
                fetchQuickInfo();
            }
        }, 0);
        return () => {
            active = false;
            clearTimeout(timer);
        };
    }, [fetchQuickInfo]);

    return {
        quickInfo,
        loading,
        upsertQuickInfo,
        deleteQuickInfo,
        refetch: fetchQuickInfo,
    };
};
